import { type AxiosInstance, type AxiosError, HttpStatusCode } from "axios";
import useSWR from "swr";
import type { KeyedMutator } from "swr";
import type {
  GetCommercialMonitoringSummariesAPIRequest,
  GetCommercialMonitoringSummariesAPIResponse,
} from "@/features/commercialRegistrationMonitoring/types";
import { SendToTrackingService } from "@/api/handlers/errors";

interface IResponse {
  data: GetCommercialMonitoringSummariesAPIResponse | undefined;
  isLoading: boolean;
  mutate: KeyedMutator<GetCommercialMonitoringSummariesAPIResponse>;
  error: AxiosError | undefined;
}

/**
 * モニタリング詳細画面に表示するデータを取得する
 * @param {AxiosInstance} apiClient
 * @param {GetCommercialMonitoringSummariesAPIRequest} requests
 * @returns {IResponse}
 */
export const GetCommercialMonitoringListAPI = (
  apiClient: AxiosInstance,
  requests: GetCommercialMonitoringSummariesAPIRequest | undefined
): IResponse => {
  const fetcher = async (
    url: string,
    params: GetCommercialMonitoringSummariesAPIRequest
  ): Promise<GetCommercialMonitoringSummariesAPIResponse> => {
    // NOTE: APIのURLパラメータの配列の表現形式（同じキーの繰り返し）に合わせるためURLSearchParamsを使用。
    // キー名はapiClientのcase変換ミドルウェア（applyCaseMiddleware）がリクエスト時にsnake_caseへ変換する。
    const searchParams = new URLSearchParams(
      [
        ["createdDateEnd", params.createdDateEnd ?? ""],
        ["createdDateStart", params.createdDateStart ?? ""],
      ].concat(
        params.receptionReasonId?.map((id) => {
          return ["receptionReasonId", String(id)];
        }) ?? []
      )
    );

    const { data } =
      await apiClient.get<GetCommercialMonitoringSummariesAPIResponse>(url, {
        params: searchParams,
      });
    return data;
  };

  const { data, isLoading, mutate, error } = useSWR<
    GetCommercialMonitoringSummariesAPIResponse,
    AxiosError
  >(
    ["/commercial-monitoring/", requests],
    async ([url, params]: [
      string,
      GetCommercialMonitoringSummariesAPIRequest
    ]) => await fetcher(url, params),
    {
      // NOTE: バックエンドの120秒制限に合わせる
      errorRetryInterval: 120_000,
      revalidateOnFocus: false,
    }
  );

  if (
    error &&
    (error.response?.status ?? HttpStatusCode.InternalServerError) >=
      HttpStatusCode.InternalServerError
  ) {
    SendToTrackingService(error);
    return { data: undefined, isLoading, mutate, error };
  }

  return { data, isLoading, mutate, error };
};
